import { decodeString } from "@findemdev/findem-helpers";
import type { IOrgV2 } from "../../types/orgs";
import type { IFetchAllUsersResponse } from "../../types/users";
import type { IFilterOption, IRaasProjectUser } from "./types";

export const findRaasAccountDefaultJoid = ({
  accountId,
  allOrgs,
  allUsers,
}: {
  accountId?: string | null;
  allOrgs?: IOrgV2[];
  allUsers?: IFetchAllUsersResponse;
}): string | undefined => {
  if (!accountId) return undefined;

  return (
    allOrgs?.find((orgUser) => orgUser.account_id === accountId)?.id ||
    allUsers?.users.find((user) => user.account_id === accountId)?._id
  );
};

export const getRaasAccountDefaultJoid = ({
  accountId,
  allOrgs,
  allUsers,
  currentUserAccountId,
  currentUserId,
}: {
  accountId?: string | null;
  allOrgs?: IOrgV2[];
  allUsers?: IFetchAllUsersResponse;
  currentUserAccountId?: string;
  currentUserId: string;
}): string => {
  if (!accountId || accountId === currentUserAccountId) {
    return currentUserId;
  }

  return (
    findRaasAccountDefaultJoid({ accountId, allOrgs, allUsers }) ||
    currentUserId
  );
};

export const getRaasOrgOptions = ({
  allOrgs,
  currentUserAccountId,
  currentUserCompany,
}: {
  allOrgs?: IOrgV2[];
  currentUserAccountId?: string;
  currentUserCompany?: string;
}): IFilterOption[] => {
  const optionsByAccountId = new Map<string, IFilterOption>();

  (allOrgs || []).forEach((org) => {
    if (!org.account_id || optionsByAccountId.has(org.account_id)) return;

    optionsByAccountId.set(org.account_id, {
      label: `${decodeString(org.org_name)} (${org.account_id})`,
      value: org.account_id,
    });
  });

  if (currentUserAccountId && !optionsByAccountId.has(currentUserAccountId)) {
    optionsByAccountId.set(currentUserAccountId, {
      label: `${currentUserCompany || "Current org"} (${currentUserAccountId})`,
      value: currentUserAccountId,
    });
  }

  return Array.from(optionsByAccountId.values()).sort((left, right) =>
    left.label.localeCompare(right.label),
  );
};

export const getRaasUserOptions = ({
  allOrgs,
  allUsers,
  canUseOrgSelector,
  currentUserAccountId,
  currentUserEmail,
  currentUserId,
  currentUserName,
  selectedAccountId,
}: {
  allOrgs?: IOrgV2[];
  allUsers?: IFetchAllUsersResponse;
  canUseOrgSelector: boolean;
  currentUserAccountId?: string;
  currentUserEmail?: string;
  currentUserId?: string;
  currentUserName?: string;
  selectedAccountId?: string;
}): IFilterOption[] => {
  const accountAdminUserOptions = (allOrgs || [])
    .filter((orgUser) => orgUser.account_id === selectedAccountId)
    .map((orgUser) => ({
      label: decodeString(orgUser.name || orgUser.email || "Unknown User"),
      value: orgUser.id,
    }));
  const accountAdminUserIds = new Set(
    accountAdminUserOptions.map((option) => option.value),
  );
  const sourceUsers =
    canUseOrgSelector && selectedAccountId
      ? (allUsers?.users || []).filter(
          (user) =>
            user.account_id === selectedAccountId &&
            !accountAdminUserIds.has(user._id),
        )
      : allUsers?.users || [];
  const userOptions = sourceUsers.map((user) => ({
    label: `${user.local?.name || "Unknown User"} (${user.local?.email || "No email"})`,
    value: user._id,
  }));
  const scopedOptions = canUseOrgSelector
    ? [...accountAdminUserOptions, ...userOptions]
    : userOptions;

  if (
    currentUserId &&
    (!canUseOrgSelector || selectedAccountId === currentUserAccountId) &&
    !scopedOptions.some((option) => option.value === currentUserId)
  ) {
    return [
      {
        label: `${currentUserName || "Current User"} (${currentUserEmail || "No email"})`,
        value: currentUserId,
      },
      ...scopedOptions,
    ];
  }

  return scopedOptions;
};

/**
 * Every user the portal knows about, keyed by id, with no account scoping.
 * `getRaasUserOptions` narrows to one account for the org/user pickers, but the
 * Assigned To column and the unresolved-option fallback need the full set: an
 * `acting_user_id` or a route `joid` can point outside the selected account.
 */
export const getRaasUsersById = ({
  allOrgs,
  allUsers,
}: {
  allOrgs?: IOrgV2[];
  allUsers?: IFetchAllUsersResponse;
}): Map<string, IRaasProjectUser> => {
  const users = new Map<string, IRaasProjectUser>();

  (allOrgs || []).forEach((user) => {
    users.set(user.id, {
      email: user.email || undefined,
      id: user.id,
      name: user.name || "",
    });
  });
  (allUsers?.users || []).forEach((user) => {
    const existingUser = users.get(user._id);
    users.set(user._id, {
      email: user.local?.email || existingUser?.email,
      id: user._id,
      name: user.local?.name || existingUser?.name || "",
    });
  });

  return users;
};

/**
 * Keeps a selected user id selectable even when it is absent from the scoped
 * option list — during the transient between an org switch and the user refetch,
 * or for a user outside the currently scoped account. Without it a Select shows
 * the bare id for a value it cannot match.
 *
 * `usersById` supplies the display name for such a user; the raw id is only used
 * when no source knows the user at all.
 */
export const withRaasUnresolvedUserOption = ({
  selectedUserId,
  userOptions,
  usersById,
}: {
  selectedUserId?: string;
  userOptions: IFilterOption[];
  usersById?: Map<string, IRaasProjectUser>;
}): IFilterOption[] => {
  if (
    !selectedUserId ||
    userOptions.some((option) => option.value === selectedUserId)
  ) {
    return userOptions;
  }

  const knownUser = usersById?.get(selectedUserId);
  const name = decodeString(knownUser?.name || "");
  const label = name
    ? `${name}${knownUser?.email ? ` (${knownUser.email})` : ""}`
    : selectedUserId;

  return [{ label, value: selectedUserId }, ...userOptions];
};
